import React, { useState, useCallback, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useZxing } from "react-zxing";
import moment from "moment";

const BarcodeScanner = ({
  onScan,
  onConsume,
  onForceConsume,
  scanResult,
  loading,
  error,
  clearError,
  scanMode,
  setScanMode,
  locations,
  locationsLoading,
  selectedLocationId,
  onLocationChange,
  onOpenUntrackedModal,
  isAdmin
}) => {
  const [manualBarcode, setManualBarcode] = useState("");
  const [purpose, setPurpose] = useState("REGULAR_USE");
  const [notes, setNotes] = useState("");
  const [consumptionType, setConsumptionType] = useState(null); // null, "total", "partial"
  const [partialQuantity, setPartialQuantity] = useState("");
  const [scannedBarcode, setScannedBarcode] = useState(null);
  const [lookupResult, setLookupResult] = useState(null); // persists phase 1 result during phase 2 loading
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showBatchDetails, setShowBatchDetails] = useState(false);
  const [showFifoWarningModal, setShowFifoWarningModal] = useState(false);

  // Camera scanner setup - pause when in consumption selection mode
  const { ref } = useZxing({
    onDecodeResult(result) {
      if (scanMode === 'camera' && selectedLocationId && !scannedBarcode) {
        handleScan(result.getText());
      }
    },
    paused: scanMode !== 'camera' || loading || !selectedLocationId || scannedBarcode !== null,
  });

  // Phase 1: Lookup only — accept numeric Inventory IDs (from QR labels)
  const handleScan = useCallback((barcode) => {
    if (barcode && !loading && selectedLocationId && /^\d+$/.test(barcode)) {
      setScannedBarcode(barcode);
      setConsumptionType(null);
      setPartialQuantity("");
      setPurpose("REGULAR_USE");
      setNotes("");
      setLookupResult(null);
      setShowBatchDetails(false);
      onScan(barcode, selectedLocationId);
      setManualBarcode("");
    }
  }, [onScan, selectedLocationId, loading]);

  // Handle manual barcode submission (phase 1 lookup)
  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualBarcode.trim() && selectedLocationId) {
      handleScan(manualBarcode.trim());
    }
  };

  // Phase 2: Consume
  const handleConsume = () => {
    if (!scannedBarcode || !consumptionType) return;
    const qty = consumptionType === 'partial' ? parseFloat(partialQuantity) : undefined;
    onConsume(scannedBarcode, selectedLocationId, consumptionType, qty, purpose, notes);
  };

  // Cancel consumption - go back to scanner
  const handleCancelConsume = () => {
    setScannedBarcode(null);
    setConsumptionType(null);
    setPartialQuantity("");
    setLookupResult(null);
    clearError();
  };

  // Close success modal and reset consumption state
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setScannedBarcode(null);
    setConsumptionType(null);
    setPartialQuantity("");
    setLookupResult(null);
  };

  // Store lookup result in local state when phase 1 succeeds
  useEffect(() => {
    if (scanResult && scanResult.stock && !scanResult.consumption && !scanResult._fifoBlocked) {
      setLookupResult(scanResult);
    }
  }, [scanResult]);

  // Show success modal when consumption happens or stock adjustment succeeds
  useEffect(() => {
    if (scanResult && (scanResult.consumption || scanResult.success)) {
      setShowSuccessModal(true);
    }
  }, [scanResult]);

  // Show FIFO warning modal when scan is blocked by server (409)
  useEffect(() => {
    if (scanResult?._fifoBlocked) {
      setShowFifoWarningModal(true);
    } else {
      setShowFifoWarningModal(false);
    }
  }, [scanResult]);

  // Reset scannedBarcode on phase 1 error so scanner reappears
  useEffect(() => {
    if (error && !lookupResult) {
      setScannedBarcode(null);
    }
  }, [error, lookupResult]);

  // Reset consumption state when location changes
  useEffect(() => {
    setScannedBarcode(null);
    setLookupResult(null);
    setConsumptionType(null);
    setPartialQuantity("");
  }, [selectedLocationId]);

  // FIFO override — container already has the original scan params stored
  const handleFifoOverride = () => {
    setShowFifoWarningModal(false);
    onForceConsume();
  };

  const handleFifoCancel = () => {
    setShowFifoWarningModal(false);
    setScannedBarcode(null);
    setConsumptionType(null);
    setPartialQuantity("");
    setLookupResult(null);
    clearError();
  };

  // Unit label from scan result or lookup result
  const unitLabel = scanResult?.unit_info?.measure_unit_display
    || lookupResult?.unit_info?.measure_unit_display
    || scanResult?.stock?.measure_unit_display
    || 'units';

  // Determine FIFO banner style for multi-batch results (after consumption)
  const isOldestBatch = scanResult?.fifo_check?.is_oldest_batch;
  const hasMultipleBatches = scanResult?.batches?.count > 1;
  const isFifoBlocked = scanResult?._fifoBlocked;
  const isFifoOverridden = scanResult?._fifoOverridden;

  // Whether we're showing the consumption panel (phase 1 complete, awaiting user choice)
  const showConsumptionPanel = scannedBarcode && lookupResult && !scanResult?.consumption && !isFifoBlocked;

  return (
    <>
      <div className="row">
        {/* Left Column - Scanner */}
        <div className="col-12 col-lg-8">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>
                <i className="fa fa-qrcode mr-05"></i>
                QR Scanner
              </span>
              <div>
                <button
                  className={`btn btn-sm ${scanMode === 'camera' ? 'btn-primary' : 'btn-outline-secondary'} mr-05`}
                  onClick={() => setScanMode('camera')}
                  disabled={!selectedLocationId}
                >
                  <i className="fa fa-camera mr-05"></i>
                  Camera
                </button>
                <button
                  className={`btn btn-sm ${scanMode === 'manual' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setScanMode('manual')}
                  disabled={!selectedLocationId}
                >
                  <i className="fa fa-keyboard-o mr-05"></i>
                  Manual
                </button>
              </div>
            </div>

            <div className="card-body py-3 px-2">
              {/* Location Selector - Admin only (market users have location resolved server-side) */}
              {isAdmin && (
                <div className="row mb-3">
                  <div className="col-12 col-md-6">
                    <label className="small mb-1">
                      <i className="fa fa-map-marker mr-05"></i>
                      Market Location <span className="text-danger">*</span>
                    </label>
                    {locationsLoading ? (
                      <div className="form-control form-control-sm text-muted">
                        <i className="fa fa-spinner fa-spin mr-05"></i> Loading...
                      </div>
                    ) : (
                      <select
                        className="form-control form-control-sm"
                        value={selectedLocationId || ""}
                        onChange={(e) => onLocationChange(e.target.value ? parseInt(e.target.value) : null)}
                        disabled={loading}
                      >
                        <option value="">-- Select a location --</option>
                        {Array.isArray(locations) && locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              )}

              {!selectedLocationId && (
                <div className="alert alert-warning py-2 mb-2" role="alert">
                  <i className="fa fa-exclamation-triangle mr-05"></i>
                  Please select a market location before scanning.
                </div>
              )}

              {error && (
                <div className="alert alert-danger alert-dismissible py-2 mb-2" role="alert">
                  <button type="button" className="btn-close" onClick={clearError}></button>
                  <i className="fa fa-times-circle mr-05"></i>
                  {error}
                </div>
              )}

              {/* Scanner section - shown when NOT in consumption selection mode */}
              {selectedLocationId && !scannedBarcode && (
                <>
                  {/* Camera Scanner */}
                  {scanMode === 'camera' && (
                    <div className="text-center mb-2 p-2" style={{ backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                      <video
                        ref={ref}
                        style={{
                          width: '100%',
                          maxWidth: '350px',
                          height: 'auto',
                          border: '2px solid #dbdee0',
                          borderRadius: '4px'
                        }}
                      />
                      <p className="text-muted mt-1 mb-0 small">
                        <i className="fa fa-crosshairs mr-05"></i>
                        Point camera at QR code to scan
                      </p>
                    </div>
                  )}

                  {/* Manual Entry */}
                  {scanMode === 'manual' && (
                    <div className="mb-2">
                      <label className="small mb-1">
                        <i className="fa fa-qrcode mr-05"></i>
                        Enter Inventory ID
                      </label>
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Inventory ID (numeric)..."
                          value={manualBarcode}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || /^\d+$/.test(val)) {
                              setManualBarcode(val);
                            }
                          }}
                          disabled={loading}
                          onKeyPress={(e) => e.key === 'Enter' && handleManualSubmit(e)}
                        />
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={handleManualSubmit}
                          disabled={loading || !manualBarcode.trim()}
                        >
                          {loading ? (
                            <i className="fa fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fa fa-search"></i>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Loading indicator during scan lookup */}
              {loading && !showConsumptionPanel && scannedBarcode && (
                <div className="text-center py-3">
                  <i className="fa fa-spinner fa-spin fa-2x"></i>
                  <p className="mt-2 text-muted small">Looking up item...</p>
                </div>
              )}

              {/* Consumption Panel - shown after scan lookup (phase 1 complete) */}
              {showConsumptionPanel && (
                <div className="card mt-0 mb-0" style={{ backgroundColor: '#f9f9f9', border: '2px solid #bdea74' }}>
                  <div className="card-header py-2" style={{ backgroundColor: '#bdea74' }}>
                    <strong style={{ fontSize: '13px' }}>
                      <i className="fa fa-cube mr-05"></i>
                      {lookupResult.stock.item_name}
                      {lookupResult.stock.brand_name && (
                        <span className="text-muted"> — {lookupResult.stock.brand_name}</span>
                      )}
                    </strong>
                  </div>
                  <div className="card-body py-2 px-3">
                    {/* Stock info */}
                    <div className="alert alert-info py-2 mb-3 small">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>
                          <i className="fa fa-cubes mr-05"></i>
                          <strong>Available:</strong>{' '}
                          <span className="badge badge-pill badge-info" style={{ fontSize: '13px' }}>
                            {lookupResult.stock.current_quantity} {unitLabel}
                          </span>
                        </span>
                        {lookupResult.batches && (
                          <span className="text-muted">
                            {lookupResult.batches.count} {lookupResult.batches.count === 1 ? 'batch' : 'batches'}
                            {lookupResult.batches.count > 1 && (
                              <span> — total: {lookupResult.batches.total_quantity} {unitLabel}</span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* FIFO Info Banner within consumption panel */}
                    {lookupResult.batches?.count > 1 && lookupResult.fifo_check?.is_oldest_batch === true && (
                      <div className="alert mb-3 py-2" style={{ backgroundColor: '#d1ecf1', borderColor: '#bee5eb' }}>
                        <small style={{ color: '#0c5460' }}>
                          <i className="fa fa-check-circle mr-05"></i>
                          <strong>FIFO OK</strong> — Using the oldest of {lookupResult.batches.count} batches.
                        </small>
                        <button
                          className="btn btn-sm btn-outline-info ms-2"
                          onClick={() => setShowBatchDetails(!showBatchDetails)}
                          style={{ fontSize: '11px', padding: '1px 6px' }}
                        >
                          <i className={`fa fa-${showBatchDetails ? 'chevron-up' : 'chevron-down'} mr-05`}></i>
                          {showBatchDetails ? 'Hide' : 'Show'} batches
                        </button>
                        {showBatchDetails && (
                          <table className="table table-striped table-sm mt-2 mb-0" style={{ fontSize: '12px' }}>
                            <thead>
                              <tr>
                                <th>Received</th>
                                <th className="text-center">Qty Remaining</th>
                                <th className="text-center">Expires</th>
                                <th className="text-center">Days Left</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lookupResult.batches.all.map((batch, index) => (
                                <tr key={batch.id || index} className={index === 0 ? 'table-success' : ''}>
                                  <td>
                                    {moment(batch.received_date).format('MM/DD/YY')}
                                    {index === 0 && (
                                      <span className="badge badge-pill badge-success ms-1" style={{ fontSize: '9px' }}>oldest</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    <strong>{batch.current_quantity}</strong> {unitLabel}
                                  </td>
                                  <td className="text-center">
                                    {batch.expiration_date
                                      ? moment(batch.expiration_date).format('MM/DD/YY')
                                      : <span className="text-muted">N/A</span>
                                    }
                                  </td>
                                  <td className="text-center">
                                    {batch.days_until_expiration != null ? (
                                      <span className={`badge badge-pill ${
                                        batch.days_until_expiration <= 3 ? 'badge-danger' :
                                        batch.days_until_expiration <= 7 ? 'badge-warning' : 'badge-info'
                                      }`}>
                                        {batch.days_until_expiration}d
                                      </span>
                                    ) : (
                                      <span className="text-muted">N/A</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}

                    {/* Consumption type selector */}
                    {!consumptionType && (
                      <>
                        <div className="d-flex gap-2 mb-2">
                          <button
                            className="btn btn-danger flex-grow-1"
                            onClick={() => setConsumptionType('total')}
                            disabled={loading}
                          >
                            <i className="fa fa-check-circle mr-05"></i>
                            Consume All ({lookupResult.stock.current_quantity} {unitLabel})
                          </button>
                          <button
                            className="btn btn-primary flex-grow-1"
                            onClick={() => setConsumptionType('partial')}
                            disabled={loading}
                          >
                            <i className="fa fa-scissors mr-05"></i>
                            Partial Consume
                          </button>
                        </div>
                        <div className="text-center">
                          <button
                            className="btn btn-link btn-sm text-muted"
                            onClick={handleCancelConsume}
                          >
                            <i className="fa fa-arrow-left mr-05"></i>
                            Back to scanner
                          </button>
                        </div>
                      </>
                    )}

                    {/* Total consumption confirmation */}
                    {consumptionType === 'total' && (
                      <div className="alert alert-warning py-2 mb-3 small">
                        <i className="fa fa-exclamation-triangle mr-05"></i>
                        This will consume <strong>all {lookupResult.stock.current_quantity} {unitLabel}</strong> of this batch.
                      </div>
                    )}

                    {/* Partial quantity input */}
                    {consumptionType === 'partial' && (
                      <div className="mb-3">
                        <label className="small mb-1">
                          <strong>Enter amount ({unitLabel})</strong>
                        </label>
                        <div className="input-group input-group-sm">
                          <input
                            type="number"
                            className="form-control"
                            min="0.1"
                            step="0.1"
                            max={lookupResult.stock.current_quantity}
                            placeholder={`Amount in ${unitLabel}...`}
                            value={partialQuantity}
                            onChange={(e) => setPartialQuantity(e.target.value)}
                            disabled={loading}
                            autoFocus
                          />
                          <span className="input-group-text">{unitLabel}</span>
                        </div>
                        <small className="text-muted">
                          Available: {lookupResult.stock.current_quantity} {unitLabel}
                        </small>
                      </div>
                    )}

                    {/* Purpose, Notes & Action buttons - shown after type selection */}
                    {consumptionType && (
                      <>
                        <div className="row mb-2">
                          <div className="col-12 col-md-6 mb-2 mb-md-0">
                            <label className="small mb-1">Purpose</label>
                            <select
                              className="form-control form-control-sm"
                              value={purpose}
                              onChange={(e) => setPurpose(e.target.value)}
                              disabled={loading}
                            >
                              <option value="REGULAR_USE">Regular Use</option>
                              <option value="EXPIRED">Expired</option>
                              <option value="DAMAGED">Damaged</option>
                              <option value="TRANSFER">Transfer</option>
                              <option value="ADJUSTMENT">Adjustment</option>
                            </select>
                          </div>
                          <div className="col-12 col-md-6">
                            <label className="small mb-1">Notes (Optional)</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Additional notes..."
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              disabled={loading}
                            />
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-secondary flex-grow-1"
                            onClick={handleCancelConsume}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-success flex-grow-1"
                            onClick={handleConsume}
                            disabled={loading || (consumptionType === 'partial' && (!partialQuantity || parseFloat(partialQuantity) <= 0))}
                          >
                            {loading ? (
                              <>
                                <i className="fa fa-spinner fa-spin mr-05"></i>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-check mr-05"></i>
                                {consumptionType === 'total'
                                  ? 'Confirm Consume All'
                                  : `Consume ${partialQuantity || '...'} ${unitLabel}`}
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Recent Activity & Status */}
        <div className="col-12 col-lg-4">
          {/* Status Card */}
          <div className="card mb-2">
            <div className="card-header py-2">
              <i className="fa fa-info-circle mr-05"></i>
              Scanner Status
            </div>
            <div className="card-body p-0">
              <table className="table table-striped table-sm mb-0">
                <tbody>
                  {isAdmin && (
                    <tr>
                      <td className="ps-2"><strong>Location</strong></td>
                      <td className="text-end pe-2">
                        {selectedLocationId ? (
                          <span className="badge badge-pill badge-success">
                            {locations?.find(l => l.id === selectedLocationId)?.name || 'Selected'}
                          </span>
                        ) : (
                          <span className="badge badge-pill badge-warning">Not Selected</span>
                        )}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="ps-2"><strong>Mode</strong></td>
                    <td className="text-end pe-2">
                      <span className="badge badge-pill badge-info">
                        {scanMode === 'camera' ? 'Camera' : 'Manual Entry'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="ps-2"><strong>Status</strong></td>
                    <td className="text-end pe-2">
                      {loading ? (
                        <span className="badge badge-pill badge-warning">
                          <i className="fa fa-spinner fa-spin mr-05"></i> Processing
                        </span>
                      ) : scannedBarcode ? (
                        <span className="badge badge-pill badge-info">Item Scanned</span>
                      ) : (
                        <span className="badge badge-pill badge-success">Ready</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Last Scan Result - New scan endpoint format */}
          {scanResult && scanResult.stock && (
            <div className="card mb-2">
              <div className="card-header py-2" style={{
                backgroundColor: isFifoBlocked ? '#ffc107' : isFifoOverridden ? '#fff3cd' : '#bdea74'
              }}>
                <i className={`fa fa-${isFifoBlocked ? 'exclamation-triangle' : 'check-circle'} mr-05`}></i>
                Last Scan {scanResult.consumption ? (isFifoOverridden ? '- Consumed (FIFO Override)' : '- Consumed') : isFifoBlocked ? '- FIFO Blocked' : '- Info'}
              </div>
              <div className="card-body py-2">
                <p className="mb-1 small">
                  <strong>Item:</strong> {scanResult.stock.item_name || 'N/A'}
                </p>
                {scanResult.stock.brand_name && (
                  <p className="mb-1 small">
                    <strong>Brand:</strong> {scanResult.stock.brand_name}
                  </p>
                )}
                {scanResult.consumption?.inventory_id && (
                  <p className="mb-1 small">
                    <strong>Inventory ID:</strong> <code>{scanResult.consumption.inventory_id}</code>
                  </p>
                )}
                {scanResult.consumption && (
                  <p className="mb-1 small">
                    <strong>Consumed:</strong> {scanResult.consumption.quantity || scanResult.consumption.quantity_consumed} {unitLabel}
                  </p>
                )}
                <p className="mb-1 small">
                  <strong>Batch Remaining:</strong>{' '}
                  <span className={`badge badge-pill ${
                    scanResult.stock.current_quantity > 10 ? 'badge-success' :
                    scanResult.stock.current_quantity > 0 ? 'badge-warning' : 'badge-danger'
                  }`}>
                    {scanResult.stock.current_quantity} {unitLabel}
                  </span>
                </p>
                {scanResult.stock.received_date && (
                  <p className="mb-1 small">
                    <strong>Received:</strong> {moment(scanResult.stock.received_date).format('MM/DD/YYYY')}
                  </p>
                )}
                {scanResult.batches && (
                  <p className="mb-0 small">
                    <strong>Total Stock ({scanResult.batches.count} {scanResult.batches.count === 1 ? 'batch' : 'batches'}):</strong>{' '}
                    {scanResult.batches.total_quantity} {unitLabel}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Last Scan Result - Stock adjustment format */}
          {scanResult && scanResult.success && !scanResult.stock && (
            <div className="card mb-2">
              <div className="card-header py-2" style={{ backgroundColor: '#bdea74' }}>
                <i className="fa fa-check-circle mr-05"></i>
                Last Scan - Success
              </div>
              <div className="card-body py-2">
                <p className="mb-1 small">
                  <strong>Item:</strong> {scanResult.item_name || 'N/A'}
                </p>
                <p className="mb-1 small">
                  <strong>Adjusted:</strong> {scanResult.quantity_reduced} {unitLabel}
                </p>
                <p className="mb-0 small">
                  <strong>Remaining:</strong>{' '}
                  <span className={`badge badge-pill ${
                    scanResult.remaining_quantity > 10 ? 'badge-success' :
                    scanResult.remaining_quantity > 0 ? 'badge-warning' : 'badge-danger'
                  }`}>
                    {scanResult.remaining_quantity} {unitLabel}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* FIFO Notice - Right Column (only after consumption, not during pending) */}
          {hasMultipleBatches && !isFifoBlocked && scanResult?.consumption && (
            <div className="card mb-2">
              <div className="card-header py-2" style={{
                backgroundColor: isOldestBatch ? '#17a2b8' : '#fabb3d',
                color: isOldestBatch ? '#fff' : '#34383c'
              }}>
                <i className={`fa fa-${isOldestBatch ? 'info-circle' : 'exclamation-triangle'} mr-05`}></i>
                {isOldestBatch ? 'FIFO Confirmed' : 'FIFO Notice'} - {scanResult.batches.count} Batches
              </div>
              <div className="card-body py-2">
                {isOldestBatch ? (
                  <div className="alert alert-info py-2 mb-2 small">
                    <i className="fa fa-check-circle mr-05"></i>
                    You are using the oldest batch — good to go.
                  </div>
                ) : (
                  <div className="alert alert-warning py-2 mb-2 small">
                    <i className="fa fa-info-circle mr-05"></i>
                    {scanResult.fifo_check?.message}
                  </div>
                )}
                <table className="table table-striped table-sm mb-0" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th>Inventory IDs</th>
                      <th className="text-center">Received</th>
                      <th className="text-center">Qty</th>
                      <th className="text-center">Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanResult.batches.all.map((batch, index) => (
                      <tr key={batch.id || index} className={index === 0 ? 'table-success' : ''}>
                        <td>
                          <code style={{ fontSize: '10px' }}>
                            {batch.inventory_ids?.length > 0
                              ? batch.inventory_ids.join(', ')
                              : '-'}
                          </code>
                          {index === 0 && (
                            <span className="badge badge-pill badge-success ms-1" style={{ fontSize: '9px' }}>oldest</span>
                          )}
                        </td>
                        <td className="text-center">
                          {moment(batch.received_date).format('MM/DD/YY')}
                          <small className="d-block text-muted">{batch.days_in_stock}d ago</small>
                        </td>
                        <td className="text-center">
                          <strong>{batch.current_quantity}</strong> {unitLabel}
                        </td>
                        <td className="text-center">
                          {batch.expiration_date ? (
                            <>
                              {moment(batch.expiration_date).format('MM/DD/YY')}
                              {batch.days_until_expiration != null && (
                                <span className={`badge badge-pill ms-1 ${
                                  batch.days_until_expiration <= 3 ? 'badge-danger' : 'badge-warning'
                                }`} style={{ fontSize: '9px' }}>
                                  {batch.days_until_expiration}d
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Instructions Card */}
          <div className="card">
            <div className="card-header py-2">
              <i className="fa fa-question-circle mr-05"></i>
              Quick Guide
            </div>
            <div className="card-body py-2 small">
              <ol className="mb-0 ps-3">
                {isAdmin && <li>Select your market location</li>}
                <li>Choose camera or manual entry mode</li>
                <li>Scan QR code or enter Inventory ID</li>
                <li>Choose "Consume All" or "Partial Consume"</li>
                <li>Set purpose, confirm, and review FIFO warnings</li>
              </ol>
            </div>
          </div>

          {/* Stock Adjustment Card - Admin only */}
          {isAdmin && (
            <div className="card mt-2">
              <div className="card-header py-2" style={{ backgroundColor: '#fabb3d', color: '#34383c' }}>
                <i className="fa fa-exclamation-circle mr-05"></i>
                Can't Scan?
              </div>
              <div className="card-body py-2">
                <p className="small mb-2 text-muted">
                  For items without QR codes (pre-deployment inventory, damaged labels):
                </p>
                <button
                  className="btn btn-warning btn-sm w-100"
                  onClick={onOpenUntrackedModal}
                  disabled={loading}
                >
                  <i className="fa fa-edit mr-05"></i>
                  Adjust Stock
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={handleCloseSuccessModal} centered dialogClassName="modal-logut" animation={false}>
        <div className="card mb-0">
          <div className="card-body text-center p-4">
            <div className="mb-3">
              <i className="fa fa-check-circle" style={{ fontSize: '4rem', color: '#bdea74' }}></i>
            </div>
            <h5>Success!</h5>
            <p className="text-muted mb-2">
              {scanResult?.message || 'Item scanned successfully'}
            </p>
            {scanResult?.stock && (
              <div className="text-start">
                <p className="mb-1 text-center"><strong>{scanResult.stock.item_name}</strong></p>
                <hr className="my-2" />
                {scanResult.consumption?.inventory_id && (
                  <p className="mb-1 small">
                    <i className="fa fa-qrcode mr-05"></i>
                    <strong>Inventory ID:</strong> <code>{scanResult.consumption.inventory_id}</code>
                  </p>
                )}
                <p className="mb-1 small">
                  <i className="fa fa-cube mr-05"></i>
                  <strong>This batch:</strong> {scanResult.stock.current_quantity} {unitLabel} remaining
                  (received {moment(scanResult.stock.received_date).format('MM/DD/YYYY')})
                </p>
                {scanResult.batches && (
                  <p className="mb-1 small">
                    <i className="fa fa-cubes mr-05"></i>
                    <strong>Total across all batches:</strong> {scanResult.batches.total_quantity} {unitLabel}
                    ({scanResult.batches.count} {scanResult.batches.count === 1 ? 'batch' : 'batches'})
                  </p>
                )}
                {/* Case 2: Info banner - using oldest batch with multiple batches */}
                {scanResult.batches?.count > 1 && scanResult.fifo_check?.is_oldest_batch === true && (
                  <div className="alert alert-info py-2 mt-2 mb-0 small text-start">
                    <i className="fa fa-check-circle mr-05"></i>
                    There are {scanResult.batches.count} batches at this location. You are using the oldest batch — good to go.
                  </div>
                )}
                {/* FIFO override indicator */}
                {scanResult._fifoOverridden && (
                  <div className="alert alert-warning py-2 mt-2 mb-0 small text-start">
                    <i className="fa fa-exclamation-triangle mr-05"></i>
                    FIFO overridden — consumed from a newer batch.
                  </div>
                )}
                {/* Single batch - no FIFO warning needed */}
                {scanResult.batches?.count === 1 && (
                  <div className="alert alert-success py-2 mt-2 mb-0 small text-start">
                    <i className="fa fa-check mr-05"></i>
                    Only one batch in stock.
                  </div>
                )}
              </div>
            )}
            {scanResult?.success && !scanResult?.stock && (
              <div>
                <p className="mb-1"><strong>{scanResult.item_name}</strong></p>
                <p className="mb-0">
                  <strong>Remaining:</strong>{' '}
                  <span className="badge badge-pill badge-info">
                    {scanResult.remaining_quantity} {unitLabel}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* FIFO Warning Modal - 409 blocked by server */}
      <Modal
        show={showFifoWarningModal}
        onHide={handleFifoCancel}
        centered
        size="lg"
        animation={false}
        backdrop="static"
      >
        <div className="card mb-0">
          <div className="card-header py-2" style={{ backgroundColor: '#ffc107', color: '#34383c' }}>
            <i className="fa fa-exclamation-triangle mr-05"></i>
            <strong>Older Stock Available</strong>
          </div>
          <div className="card-body p-3">
            {scanResult?.stock && (
              <>
                {scanResult.fifo_check?.message && (
                  <div className="alert alert-warning mb-3">
                    <i className="fa fa-exclamation-triangle mr-05"></i>
                    {scanResult.fifo_check.message}
                  </div>
                )}

                {scanResult.fifo_check?.oldest_batch && (
                  <>
                    <h6 className="mb-2">
                      <i className="fa fa-arrow-right mr-05"></i>
                      Oldest batch (use this first):
                    </h6>
                    <div className="card mb-3" style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb' }}>
                      <div className="card-body py-2 px-3">
                        <div className="row small">
                          <div className="col-6">
                            <strong>Received:</strong>{' '}
                            {moment(scanResult.fifo_check.oldest_batch.received_date).format('MM/DD/YYYY')}
                          </div>
                          <div className="col-6">
                            <strong>Remaining:</strong>{' '}
                            <span className="badge badge-pill badge-success">
                              {scanResult.fifo_check.oldest_batch.current_quantity} {unitLabel}
                            </span>
                          </div>
                          {scanResult.fifo_check.oldest_batch.inventory_ids?.length > 0 && (
                            <div className="col-12 mt-1">
                              <strong>Inventory IDs:</strong>{' '}
                              <code>{scanResult.fifo_check.oldest_batch.inventory_ids.join(', ')}</code>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="alert alert-light py-2 mb-3 small">
                  <i className="fa fa-info-circle mr-05"></i>
                  <strong>Scanned batch:</strong> received {moment(scanResult.stock.received_date).format('MM/DD/YYYY')},
                  {' '}{scanResult.stock.current_quantity} {unitLabel} remaining
                </div>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-secondary flex-grow-1"
                    onClick={handleFifoCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-warning flex-grow-1"
                    onClick={handleFifoOverride}
                    disabled={loading}
                  >
                    {loading ? (
                      <i className="fa fa-spinner fa-spin mr-05"></i>
                    ) : (
                      <i className="fa fa-exclamation-triangle mr-05"></i>
                    )}
                    Override FIFO
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BarcodeScanner;
